import React from "react";
import styled from "@emotion/styled";
import { withTheme } from "@emotion/react";
import { useTranslation } from "react-i18next";

import {
  Box,
  Button,
  dividerClasses,
  ListItemButton,
  Menu,
  AppBar as MuiAppBar,
  IconButton as MuiIconButton,
  Toolbar,
  Typography,
} from "@mui/material";
import Grid from "@mui/material/Grid";

import { Menu as MenuIcon } from "@mui/icons-material";

import NavbarNotificationsDropdown from "./NavbarNotificationsDropdown";
import NavbarLanguagesDropdown from "./NavbarLanguagesDropdown";
import NavbarUserDropdown from "./NavbarUserDropdown";
import NavbarThemesDropdown from "./NavbarThemesDropdown";
import NavbarDarkModeSwitch from "./NavbarDarkModeSwitch";
import { useGetOrganizationBrandingQuery } from "../../redux/slices/graphApiSlice";
import InitiateDocumentsDialog from "../dialogs/InitiateDocumentsDialog";
import InitiateVendorsDialog from "../dialogs/InitiateVendorsDialog";
import { DialogMode } from "../../types/dialogmode";
import NoteDialog from "../dialogs/NoteDialog";
import { DisplayModes } from "../../layouts/Dashboard";
import { NavLink, useNavigate } from "react-router-dom";
import useTheme from "../../hooks/useTheme";
import { THEMES } from "../../constants";
import AnalyzingContractsLoader from "../AnalyzingContractsLoader";
import useAppSelector from "../../hooks/useAppSelector";
import FirstTimeLoginDialog from "../../pages/components/FirstTimeLoginDialog";
import useAuth from "../../hooks/useAuth";

const AppBar = styled(MuiAppBar)<{ isdarkmode: string }>`
  background: ${(props) => props.theme.header.background};
  color: ${(props) => props.theme.header.color};

  // background-color: ${(props) => props.theme.palette.background.default};
  background-image: ${(props) =>
    props.isdarkmode === "true"
      ? `url("/static/img/backgrounds/dark-raster.png")`
      : `inherit`};
  background-size: 23%;
  background-position: top right;
`;

const AppBarNavButton = styled(Button)`
  // background: ${(props) => props.theme.header.background};
  color: ${(props) => props.theme.header.color};

  padding: 8px 12px;
`;

const Brand = styled(ListItemButton)<{
  component?: React.ReactNode;
  to?: string;
}>`
  font-size: ${(props) => props.theme.typography.h5.fontSize};
  font-weight: ${(props) => props.theme.typography.fontWeightMedium};
  color: ${(props) => props.theme.sidebar.header.color};
  // background-color: ${(props) => props.theme.sidebar.header.background};
  font-family: ${(props) => props.theme.typography.fontFamily};
  min-height: 56px;
  padding-left: ${(props) => props.theme.spacing(6)};
  padding-right: ${(props) => props.theme.spacing(6)};
  justify-content: center;
  cursor: pointer;
  flex-grow: 0;

  ${(props) => props.theme.breakpoints.up("sm")} {
    min-height: 64px;
  }
`;

const IconButton = styled(MuiIconButton)`
  svg {
    width: 22px;
    height: 22px;
  }
`;

type NavbarProps = {
  onDrawerToggle: React.MouseEventHandler<HTMLElement>;
  mode: DisplayModes;
  setMode: (mode: DisplayModes) => void;
};

const Navbar: React.FC<NavbarProps> = ({ onDrawerToggle, mode, setMode }) => {
  const { t } = useTranslation();
  const { theme } = useTheme();
  const navigate = useNavigate();
  const { user, method } = useAuth();

  const tenantId = useAppSelector((state) => state.auth.tenantId);

  const [anchorElNav, setAnchorElNav] = React.useState<null | HTMLElement>(
    null
  );

  const { data: organizationBranding, isSuccess: logoLoaded } =
    useGetOrganizationBrandingQuery(tenantId || "", {
      skip: method !== "MSAL",
    });

  const handleOpenNavMenu = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorElNav(event.currentTarget);
  };

  const handleMenuItemClick = (mode: DisplayModes) => {
    setAnchorElNav(null);

    if (mode === DisplayModes.MANAGEMENT) {
      navigate("/home");
    }

    setMode(mode);
  };

  const handleCloseNavMenu = () => {
    setAnchorElNav(null);
  };

  return (
    <React.Fragment>
      <AppBar
        // position="sticky"
        elevation={0}
        position="fixed"
        sx={{ zIndex: (theme) => theme.zIndex.drawer + 1 }}
        variant="outlined"
        isdarkmode={theme === THEMES.DARK ? "true" : "false"}
      >
        <Toolbar>
          <Grid
            container
            alignItems="center"
            justifyContent="space-between"
            width="100%"
          >
            <Grid container sx={{ minWidth: 300 }} alignItems="center" gap={2}>
              <NavLink to="/home">
                {user?.account?.logo ? (
                  <img
                    src={user.account.logo}
                    alt="logo"
                    height={60}
                    style={{
                      marginRight: 8,
                      paddingTop: 6,
                      maxWidth: 180,
                      objectFit: "contain",
                    }}
                  />
                ) : organizationBranding ? (
                  <img
                    src={
                      theme === THEMES.DARK
                        ? organizationBranding?.squareLogoDarkRelativeUrl
                        : organizationBranding?.bannerLogoRelativeUrl
                    }
                    alt="logo"
                    height={60}
                    style={{
                      marginRight: 8,
                      paddingTop: 6,
                      maxWidth: 180,
                      objectFit: "contain",
                    }}
                  />
                ) : (
                  <img
                    src="/static/img/logo/horizontal_logo.svg"
                    height={60}
                    style={{
                      marginRight: 8,
                      paddingTop: 6,
                      maxWidth: 180,
                      objectFit: "contain",
                    }}
                  />
                )}
              </NavLink>

              <Grid container display="flex" alignItems="center" gap={2}>
                {user?.account?.name && !user?.account?.logo && (
                  // only show the name if the account has no logo
                  <Typography variant="h6">{user.account.name}</Typography>
                )}

                <Button
                  href="https://gobeyondprocurement.outseta.com/profile?tab=planChange&stateProps=%7B%22planUid%22%3A%221QpG5gmE%22%2C%22planPaymentTerm%22%3A%22annual%22%7D#o-authenticated"
                  variant="contained"
                  color="inherit"
                  disabled={!user?.isTeamManager}
                  sx={{ textTransform: "uppercase" }}
                >
                  {t(user?.plan)}
                </Button>
              </Grid>
            </Grid>

            <Grid sx={{ display: { xs: "block", md: "none" } }}>
              <IconButton
                color="inherit"
                aria-label="Open drawer"
                onClick={onDrawerToggle}
                size="large"
              >
                <MenuIcon />
              </IconButton>
            </Grid>

            {/* <Grid>
              <Typography variant="h6" sx={{ ml: 2 }}>
                {organization?.displayName}
              </Typography>
            </Grid> */}

            {/* <Grid xs={6} md={4}>
              <AskMeAnything />
            </Grid> */}

            <Grid>
              <Box sx={{ flexGrow: 1, display: { xs: "flex", md: "none" } }}>
                <IconButton
                  size="large"
                  aria-label="account of current user"
                  aria-controls="menu-appbar"
                  aria-haspopup="true"
                  onClick={handleOpenNavMenu}
                  color="inherit"
                >
                  <MenuIcon />
                </IconButton>
                <Menu
                  id="menu-appbar"
                  anchorEl={anchorElNav}
                  anchorOrigin={{
                    vertical: "bottom",
                    horizontal: "left",
                  }}
                  keepMounted
                  transformOrigin={{
                    vertical: "top",
                    horizontal: "left",
                  }}
                  open={Boolean(anchorElNav)}
                  onClose={handleCloseNavMenu}
                  sx={{ display: { xs: "block", md: "none" } }}
                >
                  {/* {pages.map((page) => (
                    <MenuItem key={page} onClick={handleCloseNavMenu}>
                      <Typography sx={{ textAlign: "center" }}>
                        {page}
                      </Typography>
                    </MenuItem>
                  ))} */}
                </Menu>
              </Box>

              <Box
                sx={{
                  flexGrow: 1,
                  justifyContent: "center",
                  gap: 12,
                  display: { xs: "none", md: "flex" },
                }}
              >
                <AppBarNavButton
                  onClick={() => handleMenuItemClick(DisplayModes.CONNECTIONS)}
                  variant={
                    mode === DisplayModes.CONNECTIONS ? "outlined" : "text"
                  }
                >
                  {t("Connections")}
                </AppBarNavButton>

                <AppBarNavButton
                  onClick={() => handleMenuItemClick(DisplayModes.DASHBOARD)}
                  variant={
                    mode === DisplayModes.DASHBOARD ? "outlined" : "text"
                  }
                >
                  {t("Dashboard")}
                </AppBarNavButton>

                <AppBarNavButton
                  onClick={() => handleMenuItemClick(DisplayModes.MANAGEMENT)}
                  variant={
                    mode === DisplayModes.MANAGEMENT ? "outlined" : "text"
                  }
                >
                  {t("Management")}
                </AppBarNavButton>
              </Box>
            </Grid>

            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                [`& .${dividerClasses.root}`]: {
                  mx: 0.5,
                },
              }}
            >
              <FirstTimeLoginDialog mode={DialogMode.Add} />
              <InitiateDocumentsDialog mode={DialogMode.Add} iconOnly />
              <InitiateVendorsDialog mode={DialogMode.Add} iconOnly />
              <NoteDialog mode={DialogMode.Add} type="reminder" iconOnly />

              {/* 
              <AskMeAnything /> */}
              <AnalyzingContractsLoader variant="circular" />

              {/* <NavbarMessagesDropdown /> */}
              <NavbarNotificationsDropdown />
              <NavbarDarkModeSwitch />
              <NavbarThemesDropdown />
              <NavbarLanguagesDropdown />
              {/* <NavbarLevelsDropdown /> */}
              <NavbarUserDropdown />
            </Box>
          </Grid>
        </Toolbar>
      </AppBar>
    </React.Fragment>
  );
};

export default withTheme(Navbar);
